<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import {
  dropdownHeight,
  useSearchSuggestions,
} from '@/utils/composables/useSearchSuggestions';

interface Props {
  modelValue?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  search: [];
  'delete-history': [];
}>();

const historyRefs = useTemplateRef('historyRefs');
const autocompleteRefs = useTemplateRef('autocompleteRefs');

const {
  inputValue,
  handledValue,
  handlers,
  onKeyDown,
  search,
  searchHistories,
  historyFocusIndex,
  hoveredIndex,
  isDisplayedHistoryList,
  selectHistory,
  deleteHistory,
  completions,
  autocompleteFocusIndex,
  isAutocompleteEnabled,
  selectAutocomplete,
  isDropdownOpen,
} = useSearchSuggestions(
  () => props.modelValue,
  { historyRefs, autocompleteRefs },
  {
    onUpdateModelValue: value => emit('update:modelValue', value),
    onSearch: () => emit('search'),
    onDeleteHistory: () => emit('delete-history'),
  },
);

const NAVIGATION_TITLE_HEIGHT = 34;

// 入力されている状態の高さ：候補件数 + 入力のアイテム + 要約 + 2つのタイトル
const searchInputDropdownHeight = computed(
  () =>
    dropdownHeight(completions.value.length + 2) + NAVIGATION_TITLE_HEIGHT * 2,
);
</script>

<template>
  <div>
    <slot :value="inputValue" :handlers="handlers" :expanded="isDropdownOpen">
      <input
        class="c-text-regular--m c-textInput"
        :value="inputValue"
        v-on="handlers"
      />
    </slot>
    <!-- 検索履歴 -->
    <div
      v-if="isDisplayedHistoryList"
      class="dropdown"
      :class="{ open: true }"
      :style="{
        '--dropdown-height': dropdownHeight(searchHistories.length) + 'px',
      }"
    >
      <div
        v-for="(searchHistory, index) in searchHistories"
        :key="`${index}_${searchHistory.query}`"
        ref="historyRefs"
        class="item"
        role="listitem"
        :class="{ focused: historyFocusIndex === index }"
        :data-index="index"
        @click="selectHistory(index)"
        @keydown="onKeyDown"
        @mouseover="hoveredIndex = index"
        @mouseleave="hoveredIndex = -1"
      >
        <div class="item-content">
          <DgrIcon
            class="clock-icon"
            name="clock"
            size="small"
            :keep-fill="false"
          />
          <span class="c-text-regular--m search-history">{{
            searchHistory.query
          }}</span>
        </div>
        <button
          type="button"
          class="delete-button"
          aria-label="削除"
          @click.stop="deleteHistory(searchHistory.query)"
          v-if="historyFocusIndex === index || hoveredIndex === index"
        >
          <DgrIcon class="delete-icon" name="times" :size="'xs'" />
        </button>
      </div>
    </div>
    <!-- キーワード補完 -->
    <div
      v-if="isAutocompleteEnabled"
      class="dropdown"
      :class="{ open: true }"
      :style="{
        '--dropdown-height': `${searchInputDropdownHeight}px`,
      }"
    >
      <div
        :key="`${modelValue}`"
        class="item"
        role="listitem"
        :class="{ focused: autocompleteFocusIndex === -1 }"
        :data-index="-1"
        @click="search(modelValue)"
        @keydown="onKeyDown"
      >
        <div class="item-content">
          <DgrIcon
            class="search-icon"
            name="search"
            size="small"
            :keep-fill="false"
          />
          <span class="c-title-bold--m">{{ modelValue }}</span>
          <span class="c-text-regular--m">を検索</span>
        </div>
      </div>
      <p
        v-if="completions.length > 0"
        class="c-text-regular--s navigation-title"
      >
        検索候補
      </p>
      <div
        v-for="(completionItem, index) in completions"
        :key="`${index}_${completionItem.keyword}`"
        ref="autocompleteRefs"
        class="item"
        role="listitem"
        :class="{ focused: autocompleteFocusIndex === index }"
        :data-index="index"
        @click="selectAutocomplete(index)"
        @keydown="onKeyDown"
      >
        <div class="item-content">
          <DgrIcon
            class="search-icon"
            name="search"
            size="small"
            :keep-fill="false"
          />
          <span class="c-text-regular--m">{{
            completionItem.splitKeywords[0]
          }}</span>
          <span class="c-title-bold--m">{{
            completionItem.splitKeywords[1]
          }}</span>
        </div>
      </div>
      <!-- キーワードサジェストの最後の要素は要約結果画面への動線 -->
      <p class="c-text-regular--s navigation-title">要約を生成</p>
      <div
        class="item survey-item"
        role="listitem"
        :class="{ focused: autocompleteFocusIndex === completions.length }"
        :data-index="completions.length"
        @click="selectAutocomplete(completions.length)"
        @keydown="onKeyDown"
      >
        <p class="item-content">
          <DgrIcon
            name="sparkles-fill"
            size="small"
            class="spark-icon"
            :keep-fill="false"
          />
          <span class="c-text-regular--m">{{ handledValue }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dropdown {
  box-sizing: border-box;
  position: absolute;
  z-index: var(--z-dropdown);
  background: white;
  width: 100%;
  max-width: 590px; // keyword-search-bar.vueに合わせる
  max-height: 0;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  border: 1px solid $color-gray400;
  border-top: 0;
  border-radius: 0 0 4px 4px;
  // 検索窓との境目に影が乗らないよう、上辺のみ影を切り取る
  clip-path: inset(0 -6px -6px -6px);
  transition: max-height 0.1s ease-out;

  &.open {
    max-height: var(--dropdown-height);
  }
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  border: none;
  background: white;
  padding: 0 8px;
  margin: 0;

  &:hover {
    background: $color-gray200;
    cursor: pointer;
  }

  &.focused {
    background: $color-gray400;
  }

  &.survey-item {
    &:hover {
      background: $color-green100;
    }
    .icon-box {
      color: $color-green600;
    }
  }
}

.navigation-title {
  padding: 8px;
  color: $color-gray600;
  border-top: 1px solid $color-gray400;
  cursor: default;
}

.item-content {
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
}

.clock-icon,
.search-icon,
.spark-icon {
  flex-shrink: 0;
  margin-right: 8px;
}

.clock-icon,
.search-icon {
  color: $color-gray600;
}

.delete-button {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  margin: 0 8px;
}

.delete-icon {
  flex-shrink: 0;
}

.search-history {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  min-width: 0;
}
</style>
